import React from 'react';
import { Upload, Shield, Lock, BookOpen, LucideIcon } from 'lucide-react';
import { TabId, TabConfig } from '../types';
import { useVault } from '../contexts/VaultContext';

/**
 * Hook to safely get vault context (returns null outside provider)
 */
function useOptionalVault() {
  try {
    return useVault();
  } catch {
    return null;
  }
}

/**
 * Extended tab configuration with icon component
 */
export interface TabConfigWithIcon extends Omit<TabConfig, 'icon'> {
  id: TabId;
  label: string;
  icon: LucideIcon;
  description?: string;
}

/**
 * Props interface for the TabNavigation component
 */
export interface TabNavigationProps {
  /** Currently active tab ID */
  activeTab: TabId;
  /** Callback function when a tab is selected */
  onTabChange: (tabId: TabId) => void;
  /** Optional custom tab configuration */
  tabs?: TabConfigWithIcon[];
  /** Optional disabled state */
  disabled?: boolean;
  /** Optional compact mode for smaller screens */
  compact?: boolean;
  /** Optional vault count for badge (overrides context) */
  vaultCount?: number;
}

/**
 * Default tab configuration
 */
const defaultTabs: TabConfigWithIcon[] = [
  {
    id: 'upload',
    label: 'Cross Horizon',
    icon: Upload,
    description: 'Upload data to the vault',
  },
  {
    id: 'verify',
    label: 'Verify',
    icon: Shield,
    description: 'Verify existing hashes',
  },
  {
    id: 'vault',
    label: 'Vault',
    icon: Lock,
    description: 'View stored items',
  },
  {
    id: 'learn',
    label: 'Learn',
    icon: BookOpen,
    description: 'Educational content',
  },
];

/**
 * TabNavigation component - Tab bar for switching between application views
 *
 * Features:
 * - Icons from lucide-react for each tab
 * - Active state styling with gradient
 * - Smooth hover and focus transitions
 * - Accessible keyboard navigation
 * - Responsive design (compact mode for mobile)
 * - Customizable tab configuration
 */
export const TabNavigation: React.FC<TabNavigationProps> = ({
  activeTab,
  onTabChange,
  tabs = defaultTabs,
  disabled = false,
  compact = false,
  vaultCount: vaultCountProp,
}) => {
  // Get vault count from context if not provided via props
  const vaultContext = useOptionalVault();
  const vaultCount = vaultCountProp ?? vaultContext?.vaultCount ?? 0;

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    const tabCount = tabs.length;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown': {
        event.preventDefault();
        const nextIndex = (index + 1) % tabCount;
        const nextTab = tabs[nextIndex];
        if (nextTab) onTabChange(nextTab.id);
        break;
      }
      case 'ArrowLeft':
      case 'ArrowUp': {
        event.preventDefault();
        const prevIndex = (index - 1 + tabCount) % tabCount;
        const prevTab = tabs[prevIndex];
        if (prevTab) onTabChange(prevTab.id);
        break;
      }
      case 'Home': {
        event.preventDefault();
        const firstTab = tabs[0];
        if (firstTab) onTabChange(firstTab.id);
        break;
      }
      case 'End': {
        event.preventDefault();
        const lastTab = tabs[tabCount - 1];
        if (lastTab) onTabChange(lastTab.id);
        break;
      }
    }
  };

  return (
    <div
      role="tablist"
      aria-label="Main navigation"
      className={`flex ${compact ? 'flex-wrap gap-2' : 'space-x-2'}`}
    >
      {tabs.map((tab, index) => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;

        return (
          <button
            key={tab.id}
            role="tab"
            aria-selected={isActive}
            aria-controls={`tabpanel-${tab.id}`}
            id={`tab-${tab.id}`}
            tabIndex={isActive ? 0 : -1}
            disabled={disabled}
            onClick={() => onTabChange(tab.id)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            title={tab.description}
            className={`
              group relative flex items-center space-x-2
              ${compact ? 'px-3 py-2 text-sm' : 'px-4 py-2.5'}
              rounded-lg font-medium transition-all duration-300
              focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:ring-offset-2 focus:ring-offset-gray-900
              ${
                disabled
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer'
              }
              ${
                isActive
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/25'
                  : 'bg-gray-800/80 text-gray-400 hover:bg-gray-700/80 hover:text-white'
              }
            `}
          >
            {/* Icon */}
            <Icon
              size={compact ? 16 : 20}
              className={`
                transition-all duration-300
                ${isActive ? 'text-white' : 'text-gray-400 group-hover:text-purple-400'}
              `}
            />

            {/* Label */}
            <span
              className={`
                ${compact ? 'hidden sm:inline' : ''}
                transition-all duration-300
              `}
            >
              {tab.label}
            </span>

            {/* Vault count badge */}
            {tab.id === 'vault' && vaultCount > 0 && (
              <span
                data-testid="vault-badge"
                className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-purple-500/30 text-purple-200"
              >
                {vaultCount}
              </span>
            )}

            {/* Active indicator bar */}
            {isActive && (
              <div
                className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1/2 h-0.5 bg-white/50 rounded-full"
                style={{
                  animation: 'indicator-slide 0.3s ease-out',
                }}
              />
            )}

            {/* Hover glow effect */}
            {!isActive && !disabled && (
              <div
                className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                style={{
                  background:
                    'radial-gradient(ellipse at center, rgba(138, 43, 226, 0.15) 0%, transparent 70%)',
                }}
              />
            )}

            {/* Active glow effect */}
            {isActive && (
              <div
                className="absolute inset-0 rounded-lg pointer-events-none"
                style={{
                  background:
                    'radial-gradient(ellipse at center, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
                  animation: 'active-glow 2s ease-in-out infinite',
                }}
              />
            )}
          </button>
        );
      })}

      {/* Animations */}
      <style>{`
        @keyframes indicator-slide {
          from {
            transform: translateX(-50%) scaleX(0);
            opacity: 0;
          }
          to {
            transform: translateX(-50%) scaleX(1);
            opacity: 1;
          }
        }

        @keyframes active-glow {
          0%, 100% {
            opacity: 0.5;
          }
          50% {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

/**
 * Hook to get default tabs configuration
 */
export const useDefaultTabs = (): TabConfigWithIcon[] => {
  return defaultTabs;
};

export default TabNavigation;
